import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { ChangeEvent, useEffect, useState } from "react";
import {
  addLitigation,
  editSummon,
  fetchAssignedProsecutor,
} from "services/prosecutorDecipilineService";
import {
  AssignedProsecutor,
  Complaints,
  Investigation,
  Prosecutor,
  Summon,
} from "types";
import { LoadingIcon } from "utils/constant";
import { ROLES } from "utils/roles";
import "../styles/AddEvidenceModal.css";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  complaintData: Complaints;
  investigationData: Investigation;
  onRefresh: any;
  onComplaintsRefresh: any;
}

export default function SummonConfirmationModal({
  isOpen,
  setIsOpen,
  complaintData,
  investigationData,
  summonData,
  onRefresh,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const canCoordinatorConfirmSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  // const canAccusedConfirmSummon = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // const [error, setError] = useState<string | null>(null);
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  // const [showDropdown, setShowDropdown] = useState(false);
  // const [accusedProsecutorsData, setAccusedProsecutorsData] = useState<
  //   AccusedProsecutor[]
  // >([]);

  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        setProsecutorsData(data);
        // setError("");
      } catch (err) {
        // setError("Failed to load prosecutors.");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user]);

  const filtered = prosecutorsData?.filter(
    (p) =>
      p?.roles?.includes(ROLES.INSPECTION_PROSECUTOR) &&
      `${p?.user?.first_name} ${p?.user?.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
    // setShowDropdown(false);
  };

  // const handleAccusedProsecutorSelect = (
  //   accused: AccusedProsecutor,
  //   name: string
  // ) => {
  //   setFormState((prevState) => ({
  //     ...prevState,
  //     [name]: accused,
  //   }));
  //   setQuery("");
  //   // setShowDropdown(false);
  // };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!summonData) return;

    setLoading(true);

    const isAccusedConfirmed = summonData?.accused_confirmed
      ? summonData?.accused_confirmed
      : false;

    const isCoordinatorConfirmed = summonData?.cordinator_confirmed
      ? summonData?.cordinator_confirmed
      : canCoordinatorConfirmSummon;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("cordinator_confirmed", isCoordinatorConfirmed.toString());
    data.append(
      "assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      const resData = await editSummon(data, summon_id, isFormData);

      if (resData) {
        await addLitigation(
          {
            user_id_re_assign_prosecutor: formState.assigned_prosecutor_id,
            re_assign_prosecutor:
              `${formState?.assigned_prosecutor?.first_name || ""} ${
                formState?.assigned_prosecutor?.last_name || ""
              }` || "",
            accused_prosecutor_name:
              complaintData?.accused_prosecutor_name || "",
            user_id_accused_prosecutor_name:
              complaintData?.user_id_accused_prosecutor_name || "",
            accused_prosecutor_org_id:
              complaintData?.accused_prosecutor_org_id || "",
            accused_prosecutor_admin_unit_id:
              complaintData?.accused_prosecutor_admin_unit_id || "",
            assigned_prosecutor: complaintData?.assigned_prosecutor || "",
            user_id_assigned_prosecutor:
              complaintData?.user_id_assigned_prosecutor || "",
            admin_unit_id: complaintData?.admin_unit_id || "",
            org_id: complaintData?.org_id || "",
            summon: summonData?.id ?? null,
          },
          false,
        );
      }
      onRefresh();
      onComplaintsRefresh();
      setIsOpen(false);
      showToast("Confirmed", "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast("Submission error", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={"Confirm Summon"}>
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Assign Prosecutor
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="assigned_prosecutor_id"
              value={formState.assigned_prosecutor_id}
              onChange={(e) => {
                handleChange(e);
                const selected = filtered?.find(
                  (p) => p.user.id?.toString() === e.target.value?.toString(),
                );

                if (selected && selected.user) {
                  handleSelect(selected.user, "assigned_prosecutor");
                }
              }}
            >
              <option value={""}>Select Prosecutor</option>
              {filtered?.map((p) => (
                <option key={p.user.id} value={p.user.id}>
                  {p.user?.first_name} ({p.user?.last_name})
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              Submitting...
            </div>
          ) : (
            "Confirm"
          )}
        </button>
      </form>
    </SideModal>
  );
}
